import { JSX, ParentProps, Show, children, createContext, createMemo, createSignal, onCleanup, onMount, splitProps, useContext } from "solid-js";
import style from './st-tree.module.css'
import { useTree } from "./st-tree";
import { uuidv4 } from "common";
import Flexbox from "../st-flexbox/st-flexbox";

interface TreeNodeProps {
    id?: string
    label: string | JSX.Element
    icon?: string
}

export default function STTreeNode(props: ParentProps<TreeNodeProps> & JSX.HTMLElementTags['div']) {
    const [, other] = splitProps(props, ['class'])

    const c = children(() => props.children)

    const tree = useTree()
    const id = createMemo(() => props.id ?? uuidv4())
    const isExpanded = createMemo(() => tree.expandedIds().includes(id()))
    const isSelected = createMemo(() => tree.selectedId() === id())

    // Calculate depth from node ID (dot-separated path like "st.st-button.default")
    const depth = createMemo(() => {
        const nodeId = props.id ?? id()
        return nodeId ? nodeId.split('.').length : 0
    })

    const isLeaf = createMemo(() => {
        return !c()
    })

    // Detect if this node has children (is a parent)
    const isParent = createMemo(() => {
        return !isLeaf()
    })

    const selectNode = () => {
        tree.select(id())
    }

    const toggleExpanded = () => {
        tree.toggleExpanded(id())
    }

    const isMatch = createMemo(() => {
        const searchStr = tree.searchValue()
        if (typeof props.label === 'string') {
            return props.label.includes(searchStr) || props.id?.includes(searchStr)
        }
        return props.id?.includes(searchStr)
    })

    const isMatchPath = createMemo(() => {
        if (!tree.searchValue()) {
            return true
        }

        if (isMatch()) {
            return true
        }

        return tree.expandedIds().some(k => k.startsWith(props.id ?? ''))
    })

    const toggleIconClass = createMemo(() => {
        if (isExpanded()) {
            return 'fa-chevron-down'
        }

        return 'fa-chevron-right'
    })

    onMount(() => {
        if (typeof props.label === 'string') {
            tree.setLabel(id(), props.label)
        } else {
            tree.setLabel(id(), props.innerText?.toString() ?? props.id ?? '')
        }
    })

    onCleanup(() => {
        tree.removeLabel(id())
    })

    const onKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            if (isLeaf()) {
                selectNode()
            } else {
                selectNode()
                toggleExpanded()
            }
        } else if (e.key === 'ArrowRight' && !isLeaf()) {
            tree.select(id())

            if (!isExpanded()) {
                tree.toggleExpanded(id())
            }
        } else if (e.key === 'ArrowLeft' && !isLeaf()) {
            tree.select(id())

            if (isExpanded()) {
                tree.toggleExpanded(id())
            } else {
                // Move focus up a level, not sure how to do this yet
            }
        }
    }

    return (
        <Show when={isMatchPath()}>
            <div class={style['node-container']}>
                <Flexbox
                    dir="horizontal"
                    middle
                    role='button'
                    tabIndex={0}
                    class={`${style.node} ${isParent() ? style['node-parent'] : ''}`}
                    data-selected={isSelected()}
                    data-match={isMatch()}
                    data-depth={depth()}
                    data-is-parent={isParent()}
                    onClick={selectNode}
                    onKeyDown={onKeydown}
                >
                    <Show when={!isLeaf()}>
                        <span class={`fa ${style.toggle} ${toggleIconClass()}`} onClick={toggleExpanded}/>
                    </Show>
                    <Show when={props.icon}>
                        <span class={props.icon} />
                    </Show>
                    <Flexbox.Item auto>
                        {props.label}
                    </Flexbox.Item>
                </Flexbox>
                <Show when={isExpanded()}>
                    {c()}
                </Show>
            </div>
        </Show>
    )
}
